import type { Game } from '../../core/game';
import { BALANCE } from '../../data/balance';
import { readJsonPref, removePref, writeJsonPref } from '../prefs';

/**
 * Surcharges d'équilibrage du panneau Debug (COMMENTS §7).
 *
 * Le core expose `setOverrides` / `clearOverrides` / `getOverrides` (livrés par
 * l'agent core en parallèle). Tout est **détecté à l'exécution** : sans ces
 * méthodes, le formulaire reste utilisable (saisie + export JSON à envoyer au
 * développeur) mais « Appliquer » est désactivé.
 */

export interface BalanceOverrides {
  enemyHpMult?: number;
  enemyGoldMult?: number;
  expeditionDuration?: number;
  invasionDurationFactor?: number;
  throwCooldown?: number;
  damageMult?: number;
  critChance?: number;
  critMult?: number;
  hpDayGrowth?: number;
  hpLevelGrowth?: number;
  goldGrowth?: number;
  flatDamagePerDie?: number;
  spellPower?: number;
  poisonCap?: number;
  /** Multiplicateur d'or par niveau d'Expédition (COMMENTS v0.8.0 §1.1, A34). */
  goldLevelGrowth?: number;
  /** Facteur de dégâts par niveau de dé, Mult_Niveau (A111). */
  dieLevelFactor?: number;
  /** Multiplicateur des dés Corrupted, Mult_Corrupted (A111). */
  corruptedFlatFactor?: number;
}

export type OverrideKey = keyof BalanceOverrides;

export const OVERRIDES_KEY = 'diceordie.debug.overrides';

interface OverrideApi {
  setOverrides?: (o: BalanceOverrides) => void;
  clearOverrides?: () => void;
  getOverrides?: () => BalanceOverrides;
}

function api(game: Game): OverrideApi {
  return game as unknown as OverrideApi;
}

interface TaintApi {
  markTainted?: () => void;
  isTainted?: () => boolean;
}

/**
 * Marque la partie « debug » (phase 7, A87) : exclue des classements. À appeler
 * **avant** toute action qui change l'équilibrage ou donne quelque chose (or,
 * dés forgés, saut de niveau, surcharges, import de sauvegarde…). Lue
 * dynamiquement : sans `Game.markTainted` (cœur plus ancien), c'est un no-op.
 */
export function markTainted(game: Game): void {
  try {
    (game as unknown as TaintApi).markTainted?.();
  } catch {
    /* le debug ne doit jamais casser le jeu */
  }
}

/** La partie est-elle marquée debug ? Faux si le cœur ne le sait pas. */
export function isTainted(game: Game): boolean {
  try {
    return (game as unknown as TaintApi).isTainted?.() === true;
  } catch {
    return false;
  }
}

/** Le core expose-t-il déjà l'API de surcharge ? */
export function hasOverrideApi(game: Game): boolean {
  return typeof api(game).setOverrides === 'function';
}

/**
 * Constantes d'équilibrage lues de façon tolérante : l'agent core retouche
 * `src/data/balance.ts` en parallèle, une clé peut disparaître ou arriver.
 */
const B = BALANCE as unknown as Record<string, number | undefined>;

export interface OverrideField {
  key: OverrideKey;
  label: string;
  step: number;
  min?: number;
  /** Valeur effective courante, affichée en `placeholder`. */
  effective: (game: Game) => number;
}

export const OVERRIDE_FIELDS: OverrideField[] = [
  { key: 'enemyHpMult', label: 'PV des ennemis (×)', step: 0.1, min: 0, effective: () => 1 },
  { key: 'enemyGoldMult', label: 'Or des ennemis (×)', step: 0.1, min: 0, effective: () => 1 },
  {
    key: 'expeditionDuration',
    label: "Durée d'un jour (s)",
    step: 1,
    min: 1,
    effective: (g) => g.getDerivedStats().expeditionDuration,
  },
  {
    key: 'invasionDurationFactor',
    label: "Facteur de durée d'Invasion (×)",
    step: 0.1,
    min: 0.1,
    effective: () => B.invasionDurationFactor ?? 1.5,
  },
  {
    key: 'throwCooldown',
    label: 'Temps de retour du lancer (s)',
    step: 0.1,
    min: 0,
    effective: (g) => g.getDerivedStats().throwCooldown,
  },
  {
    key: 'damageMult',
    label: 'Dégâts du joueur (×)',
    step: 0.05,
    min: 0,
    effective: (g) => g.getDerivedStats().damageMult,
  },
  {
    key: 'critChance',
    label: 'Chance de critique (0–1)',
    step: 0.01,
    min: 0,
    effective: (g) => g.getDerivedStats().critChance,
  },
  {
    key: 'critMult',
    label: 'Multiplicateur critique (×)',
    step: 0.05,
    min: 0,
    effective: (g) => g.getDerivedStats().critMult,
  },
  {
    key: 'hpDayGrowth',
    label: 'Croissance des PV par jour (×)',
    step: 0.01,
    min: 1,
    effective: () => BALANCE.hpDayGrowth,
  },
  {
    // Depuis la v0.5.1, la croissance des PV par niveau est une **courbe**
    // (`BALANCE.hpLevelCurve`, générée dans `data/balance.ts`). Poser cette
    // surcharge la **désactive** et revient à une géométrique pure — c'est la
    // règle du cœur (`core/balance.ts`), signalée ici au testeur.
    key: 'hpLevelGrowth',
    label: 'Croissance des PV par niveau au-delà de la courbe (×) — la poser désactive la courbe',
    step: 0.1,
    min: 1,
    effective: () => BALANCE.hpLevelGrowth,
  },
  {
    key: 'goldGrowth',
    label: "Croissance de l'or par vague (×)",
    step: 0.01,
    min: 1,
    effective: () => BALANCE.goldGrowth,
  },
  {
    // COMMENTS v0.8.0 §1.1 / DECISIONS §G A34 : « valeur du multiplicateur
    // d'or par niveau d'Expédition, modifiable à tout moment dans le menu » —
    // même mécanique que les autres surcharges (Appliquer / Réinitialiser /
    // export JSON), le cœur connaît déjà la clé (`src/core/balance.ts`).
    key: 'goldLevelGrowth',
    label: "Croissance de l'or par niveau (×)",
    step: 0.05,
    min: 1,
    effective: () => BALANCE.goldLevelGrowth,
  },
  {
    key: 'flatDamagePerDie',
    label: 'Dégâts fixes par dé (+)',
    step: 1,
    min: 0,
    effective: (g) => (g.getDerivedStats() as { flatDamagePerDie?: number }).flatDamagePerDie ?? 0,
  },
  {
    key: 'spellPower',
    label: 'Puissance des sorts (×)',
    step: 0.05,
    min: 0,
    effective: (g) => g.getDerivedStats().spellPower,
  },
  {
    key: 'poisonCap',
    label: 'Plafond de poison (part des dégâts)',
    step: 0.05,
    min: 0,
    effective: () => B.poisonCap ?? 0.3,
  },
  // A111 : les deux paramètres du carnet des dés Corrupted.
  {
    key: 'dieLevelFactor',
    label: 'Niveau des dés : facteur par niveau, Mult_Niveau (×)',
    step: 0.1,
    min: 1,
    effective: () => B.dieLevelFactor ?? 2,
  },
  {
    key: 'corruptedFlatFactor',
    label: 'Dés Corrupted : multiplicateur des faces, Mult_Corrupted (×)',
    step: 100000,
    min: 1,
    effective: () => B.corruptedFlatFactor ?? 5_000,
  },
];

/** Ne garde que les nombres finis : le JSON exporté reste propre. */
export function sanitizeOverrides(raw: unknown): BalanceOverrides {
  const out: BalanceOverrides = {};
  if (typeof raw !== 'object' || raw === null) return out;
  const src = raw as Record<string, unknown>;
  for (const field of OVERRIDE_FIELDS) {
    const v = src[field.key];
    if (typeof v === 'number' && Number.isFinite(v)) out[field.key] = v;
  }
  return out;
}

export function loadStoredOverrides(): BalanceOverrides {
  return sanitizeOverrides(readJsonPref<unknown>(OVERRIDES_KEY));
}

export function storeOverrides(o: BalanceOverrides): void {
  if (Object.keys(o).length === 0) removePref(OVERRIDES_KEY);
  else writeJsonPref(OVERRIDES_KEY, o);
}

export function clearStoredOverrides(): void {
  removePref(OVERRIDES_KEY);
}

/** Surcharges réellement actives dans le core (vide si l'API manque). */
export function activeOverrides(game: Game): BalanceOverrides {
  const get = api(game).getOverrides;
  if (typeof get !== 'function') return {};
  try {
    return sanitizeOverrides(get.call(game));
  } catch {
    return {};
  }
}

/**
 * Applique des surcharges au core et les persiste. Renvoie `false` si l'API
 * manque. `setOverrides` **fusionne** côté core : on efface d'abord, sinon un
 * champ vidé dans le formulaire resterait actif.
 */
export function applyOverrides(game: Game, o: BalanceOverrides): boolean {
  // Une surcharge change l'équilibrage : la partie sort des classements (A87).
  if (Object.keys(o).length > 0) markTainted(game);
  storeOverrides(o);
  const { setOverrides: set, clearOverrides: clear } = api(game);
  if (typeof set !== 'function') return false;
  if (typeof clear === 'function') clear.call(game);
  set.call(game, o);
  return true;
}

/** Réinitialise core + stockage. Renvoie `false` si l'API manque. */
export function resetOverrides(game: Game): boolean {
  clearStoredOverrides();
  const clear = api(game).clearOverrides;
  if (typeof clear !== 'function') return false;
  clear.call(game);
  return true;
}

/**
 * Rejoue les surcharges persistées au démarrage (appelé par `src/main.ts`,
 * uniquement en mode debug) : un testeur garde ses réglages d'un rechargement
 * à l'autre. Sans API core, l'appel est un no-op silencieux.
 */
export function applyStoredOverrides(game: Game): BalanceOverrides {
  const stored = loadStoredOverrides();
  if (Object.keys(stored).length === 0) return stored;
  const set = api(game).setOverrides;
  if (typeof set === 'function') {
    markTainted(game);
    set.call(game, stored);
  }
  return stored;
}
